import { imageurl } from "../App";
import { brandLogo } from "../assets/img/logo/brandLogos";

/**
 * Returns the currently configured store logo (from localStorage cache or default asset).
 */
export const getDefaultStoreLogo = () => {
  try {
    const raw = localStorage.getItem("store_settings");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.logoUrl && typeof parsed.logoUrl === "string" && parsed.logoUrl.trim() !== "") {
        return parsed.logoUrl.startsWith("http")
          ? parsed.logoUrl
          : `${imageurl}/${parsed.logoUrl.replace(/^\/+/, "")}`;
      }
    }
  } catch {}
  try {
    return brandLogo;
  } catch {
    return "/images/logo.png";
  }
};

/**
 * Check if an image path or URL represents a default store logo or fallback placeholder
 */
export const isDefaultStoreImage = (url) => {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase().trim();

  if (
    lower === "" ||
    lower === "null" ||
    lower === "undefined" ||
    lower.includes("weblogo") ||
    lower.includes("getgrowcart_logo") ||
    lower.includes("getgrowcart_icon") ||
    lower.includes("logo_") ||
    lower.includes("/images/logo") ||
    lower.includes("images/logo") ||
    lower.includes("placeholder") ||
    lower.includes("default.png") ||
    lower.includes("default.jpg") ||
    lower.includes("no-image") ||
    lower.includes("no_image") ||
    lower.includes("og_social") ||
    lower.includes("1792072319_0") ||
    lower.includes("1791565470_0") ||
    lower.includes("1791565066_0") ||
    lower.includes("1791565132_0") ||
    lower.includes("1792681585") ||
    lower.includes("1792682832") ||
    lower.includes("1792068407") ||
    lower.includes("1792069677") ||
    lower.includes("1792071097") ||
    lower.includes("1792072399") ||
    lower.includes("gocartwhite") ||
    lower.includes("store_logo") ||
    lower.includes("storelogo")
  ) {
    return true;
  }

  // Dynamic check against currently configured store settings
  try {
    const raw = localStorage.getItem("store_settings");
    if (raw) {
      const parsed = JSON.parse(raw);
      const targets = [parsed.logoUrl, parsed.socialShareImage, parsed.faviconUrl];
      for (const t of targets) {
        if (t && typeof t === "string") {
          const cleanT = t.toLowerCase().trim();
          const baseT = cleanT.split("/").pop();
          if (baseT && baseT.length > 3 && (lower.includes(baseT) || lower.endsWith(baseT))) {
            return true;
          }
        }
      }
    }
  } catch {}

  return false;
};

/**
 * Safely extracts the image path from a product object or string.
 * Strictly prioritizes real uploaded images over default logos or placeholders.
 */
export const extractProductImagePath = (input) => {
  if (!input) return null;
  if (typeof input === "string") {
    return isDefaultStoreImage(input) ? null : input;
  }

  if (typeof input === "object") {
    const candidates = [];

    // 1. Array of images
    if (Array.isArray(input)) {
      for (const it of input) {
        const val = typeof it === "string" ? it : it?.image;
        if (val && typeof val === "string" && val.trim() !== "" && val !== "null" && val !== "undefined") {
          candidates.push(val);
        }
      }
    }

    // 2. product_image (snake_case)
    if (Array.isArray(input.product_image) && input.product_image.length > 0) {
      for (const it of input.product_image) {
        const val = typeof it === "string" ? it : it?.image;
        if (val && typeof val === "string" && val.trim() !== "" && val !== "null" && val !== "undefined") {
          candidates.push(val);
        }
      }
    }

    // 3. productImage (camelCase)
    if (Array.isArray(input.productImage) && input.productImage.length > 0) {
      for (const it of input.productImage) {
        const val = typeof it === "string" ? it : it?.image;
        if (val && typeof val === "string" && val.trim() !== "" && val !== "null" && val !== "undefined") {
          candidates.push(val);
        }
      }
    }

    // 4. Direct image attribute
    if (input.image && typeof input.image === "string" && input.image.trim() !== "" && input.image !== "null" && input.image !== "undefined") {
      candidates.push(input.image);
    }

    // 5. Direct img attribute (e.g., DataGrid rows)
    if (input.img && typeof input.img === "string" && input.img.trim() !== "" && input.img !== "null" && input.img !== "undefined") {
      candidates.push(input.img);
    }

    // First priority: find first REAL product image (not a store logo / default)
    const realCandidate = candidates.find((c) => !isDefaultStoreImage(c));
    if (realCandidate) {
      return realCandidate;
    }

    // If only store logos or defaults exist, return null so caller knows no real image exists
    return null;
  }

  return null;
};

/**
 * Resolves a product image URL.
 * Strictly displays the real product image. ONLY falls back to the Store Logo if no real image exists.
 */
export const getProductImageUrl = (input) => {
  const imagePath = typeof input === "object" ? extractProductImagePath(input) : input;

  if (
    !imagePath ||
    typeof imagePath !== "string" ||
    imagePath.trim() === "" ||
    imagePath === "null" ||
    imagePath === "undefined" ||
    isDefaultStoreImage(imagePath)
  ) {
    return getDefaultStoreLogo();
  }

  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("data:") ||
    imagePath.startsWith("blob:")
  ) {
    return imagePath;
  }

  return `${imageurl}/${imagePath.replace(/^\/+/, "")}`;
};

const faviconMime = (href) => {
  const value = href.split("?")[0].toLowerCase();
  if (value.endsWith(".svg")) return "image/svg+xml";
  if (value.endsWith(".ico")) return "image/x-icon";
  if (value.endsWith(".png")) return "image/png";
  if (value.endsWith(".webp")) return "image/webp";
  if (value.endsWith(".gif")) return "image/gif";
  if (value.endsWith(".jpg") || value.endsWith(".jpeg")) return "image/jpeg";
  return "";
};

/**
 * Sets the browser tab icon from store settings.
 * A saved favicon is used first. The store logo is used when no favicon is set.
 */
const BARE_HOME_TITLES = ["gootocart", "gootocart store", "store"];

/** Replace a leftover Gootocart brand with the live store name. */
export const connectSeoText = (text, storeName) => {
  const name = String(storeName || "").trim();
  let source = String(text || "");
  if (!source.trim() || !name || name.toLowerCase().includes("gootocart")) return source;
  if (source.toLowerCase().includes(name.toLowerCase())) {
    source = source.replace(/\s*[–—|•-]\s*gooto?cart(?:\s+store)?\s*$/i, "");
  }
  return source
    .replace(/(?<!@)(?:www\.)?getgrowcart\.com/gi, name)
    .replace(/gooto?cart(?:\s+store)?/gi, name)
    .replace(/\s{2,}/g, " ")
    .trim();
};

export const homepageSeoTitle = (settings = {}) => {
  const name = (settings.storeName || "").trim() || "Store";
  const separator = (settings.titleSeparator || "–").trim() || "–";
  const raw = (settings.metaTitle || "").trim();
  if (!raw || BARE_HOME_TITLES.includes(raw.toLowerCase())) {
    return `${name} ${separator} Online Store`;
  }
  const connected = connectSeoText(raw, name).trim();
  return connected || `${name} ${separator} Online Store`;
};

export const readCachedStoreSettings = () => {
  try {
    const raw = localStorage.getItem("store_settings");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const rememberStoreSettings = (settings) => {
  if (!settings || typeof settings !== "object") return;
  try {
    localStorage.setItem("store_settings", JSON.stringify(settings));
  } catch {}
};

/** Homepage tab title from the saved SEO title and the live store name. */
export const storeHomeTitle = (settings = {}) => homepageSeoTitle(settings);

/** Page title plus the current store name. */
export const storePageTitle = (pageLabel, settings = readCachedStoreSettings()) => {
  const name = (settings?.storeName || "").trim() || "Store";
  const label = connectSeoText(String(pageLabel || ""), name).trim();
  if (!label) return storeHomeTitle(settings);
  if (label.toLowerCase().includes(name.toLowerCase())) return label;
  const separator = (settings?.titleSeparator || "–").trim() || "–";
  return `${label} ${separator} ${name}`;
};

let activePageLabel = "";

export const applyStoreTitle = (settings, pageLabel) => {
  if (typeof document === "undefined") return;
  if (settings) rememberStoreSettings(settings);
  if (pageLabel !== undefined) {
    activePageLabel = pageLabel ? String(pageLabel) : "";
  }
  const current = settings || readCachedStoreSettings();
  document.title = activePageLabel
    ? storePageTitle(activePageLabel, current)
    : storeHomeTitle(current);
};

export const applyStoreFavicon = (settings = {}) => {
  if (typeof document === "undefined") return;
  const href = resolveMediaUrl(settings.faviconUrl) || resolveMediaUrl(settings.logoUrl);
  if (!href) return;
  const type = faviconMime(href);
  ["icon", "shortcut icon", "apple-touch-icon"].forEach((rel) => {
    document.querySelectorAll(`link[rel="${rel}"]`).forEach((node) => node.remove());
    const link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    if (type) link.type = type;
    document.head.appendChild(link);
  });
};

/** Store uploads are saved as /uploads/... and must load from the API host. */
export const resolveMediaUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const value = url.trim();
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }
  // Built-in theme artwork lives in the React public directory, not Laravel's
  // upload storage. Keep it on the storefront origin so saved theme defaults
  // continue to work in both the real demo and the customizer.
  if (value.startsWith("/assets/themes/")) return value;
  if (value.startsWith("assets/themes/")) return `/${value}`;
  return `${imageurl}/${value.replace(/^\/+/, "")}`;
};

/**
 * Standard image onError handler that falls back to the dynamic store logo.
 */
export const handleProductImageError = (e) => {
  if (!e || !e.target) return;
  e.target.onerror = null; // Prevent infinite loop if logo fails
  e.target.src = getDefaultStoreLogo();
};
